/**
 * Introduction Scene - "The Garden Wakes Up"
 *
 * Tutorial that teaches through gentle discovery.
 * Based on docs/INTRODUCTION.md
 *
 * Phases:
 * 0. Arrival - Flutter introduces herself
 * 1. First Touch - Seed interaction
 * 2. Sound Before Symbol - Sound bud appears
 * 3. Sound + Shape - Letter appears
 * 4. Gentle Blending - Two sounds together
 * 5. Fade-Out - Open world
 */

import { Application, Container, Sprite, Assets } from 'pixi.js';
import { getSoundPlayer } from '@game/audio';
import { SoundBud } from './SoundBud';
import { Seed } from './Seed';

export class Introduction {
  private app: Application;
  private container: Container;
  private flutter: Sprite | null = null;
  private seed: Seed | null = null;
  private soundBud: SoundBud | null = null;
  private flutterStartPosition: { x: number; y: number };

  // Scene elements
  private sky: Sprite | null = null;
  private ground: Sprite | null = null;
  private foliage: Sprite | null = null;

  constructor(app: Application, flutterStartPosition?: { x: number; y: number }) {
    this.app = app;
    this.container = new Container();

    // Use provided position or default (middle of screen, both X and Y)
    this.flutterStartPosition = flutterStartPosition || {
      x: app.screen.width * 0.5,
      y: app.screen.height * 0.5
    };
  }

  /**
   * Load all introduction assets
   */
  async load(): Promise<void> {
    await Assets.load([
      '/assets/title-scene/sky-background.png',
      '/assets/title-scene/ground-layer.png',
      '/assets/title-scene/foliage-canopy.png',
      '/assets/title-scene/butterfly.png', // Flutter
      '/assets/introduction/seed.png',
      '/assets/introduction/sound-bud-spritesheet.png',
      '/sounds/flutter/arrival-1.wav'
    ]);

    console.log('✓ Introduction assets loaded');
  }

  /**
   * Show introduction scene
   */
  async show(): Promise<void> {
    this.container.removeChildren();

    this.layoutScene();

    // Add to stage
    this.app.stage.addChild(this.container);

    // Start Phase 0: Arrival
    await this.phaseArrival();

    // Handle resize
    window.addEventListener('resize', () => this.handleResize());
  }

  /**
   * Layout scene elements
   */
  private layoutScene(): void {
    const screenWidth = this.app.screen.width;
    const screenHeight = this.app.screen.height;

    // Sky
    if (!this.sky) {
      this.sky = Sprite.from('/assets/title-scene/sky-background.png');
      this.container.addChild(this.sky);
    }
    this.sky.width = screenWidth;
    this.sky.height = screenHeight;

    // Ground
    if (!this.ground) {
      this.ground = Sprite.from('/assets/title-scene/ground-layer.png');
      this.container.addChild(this.ground);
    }
    this.ground.width = screenWidth;
    this.ground.height = screenHeight * 0.5;
    this.ground.y = screenHeight * 0.5;

    // Foliage
    if (!this.foliage) {
      this.foliage = Sprite.from('/assets/title-scene/foliage-canopy.png');
      this.container.addChild(this.foliage);
    }
    this.foliage.width = screenWidth;
    this.foliage.height = screenHeight * 0.4;
    this.foliage.y = 0;

    // Flutter (butterfly) - starts on left where transition ends
    if (!this.flutter) {
      this.flutter = Sprite.from('/assets/title-scene/butterfly.png');
      this.flutter.texture.source.scaleMode = 'linear';
      this.flutter.alpha = 1; // Ensure full visibility (no fade)
      this.container.addChild(this.flutter);

      // Start her gentle flight pattern (positions her immediately)
      this.startFlutterFlight();
    }

    // Match title screen butterfly size for seamless transition
    const flutterSize = Math.min(screenWidth * 0.25, 400);
    this.flutter.width = flutterSize;
    this.flutter.height = flutterSize;
    // Position handled by flight animation

    // Seed (if shown) - matches sound bud size, positioned on top of bud
    if (this.seed) {
      const seedSize = Math.min(screenWidth * 0.3, 450);
      this.seed.setSize(seedSize);
      this.seed.x = screenWidth * 0.25; // Same as sound bud
      this.seed.y = screenHeight * 0.75; // Same as sound bud
    }

    // Sound bud (if shown) - larger for mobile touch, bottom left on ground
    if (this.soundBud) {
      this.soundBud.setSize(Math.min(screenWidth * 0.3, 450));
      this.soundBud.x = screenWidth * 0.25; // Bottom left
      this.soundBud.y = screenHeight * 0.75; // On the ground
    }
  }

  /**
   * Handle resize
   */
  private handleResize(): void {
    this.layoutScene();
  }

  /**
   * Phase 0: Arrival - Show sound bud on ground
   */
  private async phaseArrival(): Promise<void> {
    console.log('🦋 Phase 0: Arrival');

    const player = getSoundPlayer();

    // Fade in scene elements (Flutter already visible from title screen)
    if (this.sky) this.sky.alpha = 0;
    if (this.ground) this.ground.alpha = 0;
    if (this.foliage) this.foliage.alpha = 0;

    // Create sound bud (visible from start, but not interactive yet)
    this.soundBud = new SoundBud();
    await this.soundBud.load();
    this.container.addChild(this.soundBud);
    this.soundBud.alpha = 0; // Start invisible, will fade in
    this.layoutScene();

    const fadeInDuration = 2000;
    const startTime = Date.now();

    await new Promise<void>((resolve) => {
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / fadeInDuration, 1);

        // Fade in background elements and sound bud
        if (this.sky) this.sky.alpha = progress;
        if (this.ground) this.ground.alpha = progress;
        if (this.foliage) this.foliage.alpha = progress;
        if (this.soundBud) this.soundBud.alpha = progress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      animate();
    });

    // Flutter speaks
    try {
      await player.play('/sounds/flutter/arrival-1.wav', {
        channel: 'speech',
        volume: 0.6
      });
    } catch (err) {
      console.warn('Flutter audio not found:', err);
    }

    // Move to Phase 1 - seed falls onto flower
    await this.phaseFirstTouch();
  }

  /**
   * Phase 1: First Touch - Seed falls onto sound bud
   */
  private async phaseFirstTouch(): Promise<void> {
    console.log('🍃 Phase 1: Seed falls onto sound bud');

    // Create and animate seed floating down onto the sound bud
    if (!this.seed) {
      this.seed = new Seed();
      await this.seed.load();

      // Match sound bud size and position
      const seedSize = Math.min(this.app.screen.width * 0.3, 450);
      this.seed.setSize(seedSize);
      this.seed.x = this.app.screen.width * 0.25; // Same X as sound bud

      // Add seed AFTER sound bud so it renders on top
      this.container.addChild(this.seed);

      // Animate seed floating down onto sound bud
      const targetY = this.app.screen.height * 0.75; // Same Y as sound bud
      await this.seed.floatDown(targetY); // 4 second slow float

      // Start subtle glow pulse after landing
      const glowAnim = this.seed.startGlowPulse();
      this.app.ticker.add(glowAnim);
    }

    // Make seed interactive
    this.seed.eventMode = 'static';
    this.seed.cursor = 'pointer';

    const player = getSoundPlayer();

    // Wait for tap with timeout prompt
    await new Promise<void>((resolve) => {
      let interacted = false;

      // After 4 seconds, Flutter gives a gentle prompt
      const promptTimeout = setTimeout(async () => {
        if (!interacted) {
          console.log('💬 Flutter prompts: "Sometimes the garden listens when you touch it."');
          try {
            await player.play('/sounds/flutter/seed-prompt.wav', {
              channel: 'speech',
              volume: 0.6
            });
          } catch (err) {
            console.warn('Prompt audio not found');
          }
        }
      }, 4000);

      this.seed!.once('pointerdown', async () => {
        // User interacted - cancel prompt
        clearTimeout(promptTimeout);
        interacted = true;
        // Wiggle seed
        await this.seed!.wiggle();

        // Play rustle sound (if available)
        try {
          await player.play('/sounds/effects/seed-rustle.wav', {
            channel: 'speech',
            volume: 0.5
          });
        } catch (err) {
          console.log('Rustle sound not found (optional)');
        }

        await this.seed!.fadeOut(500);
        await this.seed?.destroy();

        // Flutter reacts
        try {
          await player.play('/sounds/flutter/seed-wake.wav', {
            channel: 'speech',
            volume: 0.6
          });
        } catch (err) {
          console.warn('Flutter dialogue not found');
        }


        resolve();
      });
    });

    // Move to Phase 2
    await this.phaseSoundBud();
  }

  /**
   * Phase 2: Sound Before Symbol - Make sound bud interactive
   */
  private async phaseSoundBud(): Promise<void> {
    console.log('🌱 Phase 2: Sound bud becomes interactive');

    if (!this.soundBud) return;

    // Start glow pulse
    const glowAnim = this.soundBud.startGlowPulse();
    this.app.ticker.add(glowAnim);

    // Make interactive
    this.soundBud.eventMode = 'static';
    this.soundBud.cursor = 'pointer';

    const player = getSoundPlayer();

    // Flutter introduces the sound
    try {
      // "Listen…"
      await player.play('/sounds/flutter/sound-intro-1.wav', {
        channel: 'speech',
        volume: 0.6
      });

      // Demonstrate the phoneme sound
      await player.play('/sounds/phonemes/m.wav', {
        channel: 'speech',
        volume: 0.7
      });

      // "That's a sound."
      await player.play('/sounds/flutter/sound-intro-2.wav', {
        channel: 'speech',
        volume: 0.6
      });
    } catch (err) {
      console.warn('Flutter dialogue not found');
    }

    // Bud plays phoneme when tapped (repeatable)
    let firstTap = true;

    this.soundBud.on('pointerdown', async () => {
      await this.soundBud!.wiggle();

      // Play "mmm" phoneme sound
      try {
        await player.play('/sounds/phonemes/m.wav', {
          channel: 'speech',
          volume: 0.7
        });
      } catch (err) {
        console.log('Phoneme sound not found');
      }

      // Flutter reassures only on first tap
      if (firstTap) {
        firstTap = false;
        try {
          await player.play('/sounds/flutter/reassurance.wav', {
            channel: 'speech',
            volume: 0.6
          });
        } catch (err) {
          console.log('Reassurance dialogue not found');
        }
      }
    });

    // Phase 2 continues indefinitely - let child explore
    console.log('✓ Introduction scene running (Phase 2 active - explore freely)');
  }

  /**
   * Start Flutter's gentle flight pattern
   * Starts from exact handoff position, then drifts gently
   */
  private startFlutterFlight(): void {
    if (!this.flutter) return;

    console.log(`🦋 Flutter starting flight from position: x=${this.flutterStartPosition.x.toFixed(0)}, y=${this.flutterStartPosition.y.toFixed(0)}`);

    const startTime = Date.now();
    const flightSpeed = 0.0003; // Very slow
    const bobSpeed = 0.002; // Vertical bobbing

    // Set initial position exactly from handoff
    this.flutter.x = this.flutterStartPosition.x;
    this.flutter.y = this.flutterStartPosition.y;

    const centerX = this.flutterStartPosition.x; // Center drift around handoff X
    const centerY = this.flutterStartPosition.y; // Center bob around handoff Y
    const driftRange = this.app.screen.width * 0.1; // Drift ±10% of screen width

    const animate = () => {
      if (!this.flutter) return;

      const elapsed = Date.now() - startTime;

      // Gentle horizontal drift
      const horizontalDrift = Math.sin(elapsed * flightSpeed) * driftRange;

      // Vertical bobbing
      const verticalBob = Math.sin(elapsed * bobSpeed) * 20;

      // Small wobble for natural flight
      const horizontalWobble = Math.sin(elapsed * bobSpeed * 1.5) * 5;

      this.flutter.x = centerX + horizontalDrift + horizontalWobble;
      this.flutter.y = centerY + verticalBob;
    };

    this.app.ticker.add(animate);
  }

  /**
   * Clean up
   */
  hide(): void {
    this.app.stage.removeChild(this.container);
  }
}
